import React, { useState, useEffect } from 'react';
import { getVisits, getUsers, deleteVisit, updateVisit, getCustomers } from '@/api/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Calendar, User, FileText, Download, Trash2, Edit, Search, ClipboardList } from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import * as XLSX from 'xlsx';
import PageHeader from '@/components/PageHeader';

const Visits = ({ user }) => {
  const [visits, setVisits] = useState([]);
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, 7, 30
  const [repFilter, setRepFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all'); // all, visit, sale
  const [searchQuery, setSearchQuery] = useState(''); // ARAMA FİLTRESİ
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('date-desc'); // date-desc, date-asc, customer-asc, customer-desc
  const itemsPerPage = 100;
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingVisit, setEditingVisit] = useState(null);
  const [editFormData, setEditFormData] = useState({ notes: '', sale_made: false });

  useEffect(() => {
    // Kullanıcı listesi - admin, abonelik, temsilci yükleyebilir
    if (['admin', 'abonelik', 'temsilci'].includes(user?.role)) {
      loadUsers();
    }
  }, [user]);

  useEffect(() => {
    loadVisits();
    setCurrentPage(1); // Reset page when filters or sort changes
  }, [filter, repFilter, sortBy, searchQuery]);

  const loadUsers = async () => {
    try {
      const data = await getUsers();
      setUsers(data);
    } catch (error) {
      console.error('Kullanıcılar yüklenemedi');
    }
  };

  const loadVisits = async () => {
    try {
      const days = filter === 'all' ? null : parseInt(filter);
      // RepFilter kullanıldıysa filtrele, yoksa tümü
      const rep = repFilter === 'all' ? null : repFilter;
      let data = await getVisits(days, rep);
      
      // Sıralama uygula
      data = data.sort((a, b) => {
        switch(sortBy) {
          case 'date-desc':
            return new Date(b.visit_date) - new Date(a.visit_date);
          case 'date-asc':
            return new Date(a.visit_date) - new Date(b.visit_date);
          case 'customer-asc':
            return (a.customer_name || '').localeCompare(b.customer_name || '', 'tr');
          case 'customer-desc':
            return (b.customer_name || '').localeCompare(a.customer_name || '', 'tr');
          default:
            return new Date(b.visit_date) - new Date(a.visit_date);
        }
      });
      
      setVisits(data);
    } catch (error) {
      toast.error('Ziyaretler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = () => {
    try {
      // Filtre kontrolü - herhangi bir filtre uygulanmış mı?
      const isFiltered = searchQuery.trim() !== '' || 
                         statusFilter !== 'all' || 
                         repFilter !== 'all' ||
                         filter !== 'all';
      
      // Filtre varsa filtrelenmiş veriyi, yoksa tüm veriyi kullan
      // Filtrelenmiş visits'i hesapla
      const filteredVisits = visits.filter(visit => {
        // Durum filtresi
        if (statusFilter === 'sale' && visit.sale_made !== true) return false;
        if (statusFilter === 'visit' && visit.sale_made === true) return false;
        
        // Arama filtresi
        if (searchQuery.trim()) {
          const query = searchQuery.toLowerCase();
          return (
            visit.customer_name?.toLowerCase().includes(query) ||
            visit.representative?.toLowerCase().includes(query) ||
            visit.notes?.toLowerCase().includes(query)
          );
        }
        
        return true;
      });
      
      const dataToExport = isFiltered ? filteredVisits : visits;
      
      // Prepare data for export
      const exportData = dataToExport.map(visit => ({
        'Tarih': format(new Date(visit.visit_date), 'dd.MM.yyyy HH:mm', { locale: tr }),
        'Müşteri': visit.customer_name,
        'Temsilci': visit.representative,
        'Notlar': visit.notes || '',
        'Satış Yapıldı': visit.sale_made ? 'Evet' : 'Hayır'
      }));

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);
      
      // Set column widths
      ws['!cols'] = [
        { wch: 18 }, // Tarih
        { wch: 30 }, // Müşteri
        { wch: 20 }, // Temsilci
        { wch: 50 }, // Notlar
        { wch: 12 }  // Satış Yapıldı
      ];

      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Ziyaretler');

      // Generate filename
      const fileName = isFiltered 
        ? `ProGo_Ziyaretler_Filtreli_${exportData.length}_adet.xlsx`
        : `ProGo_Tum_Ziyaretler_${exportData.length}_adet.xlsx`;

      // Save file
      XLSX.writeFile(wb, fileName);
      
      toast.success(`${exportData.length} ziyaret Excel'e aktarıldı${isFiltered ? ' (filtrelenmiş)' : ' (tümü)'}`);
    } catch (error) {
      toast.error('Excel dosyası oluşturulurken hata oluştu');
    }
  };

  const handleDeleteVisit = async (visitId, customerName) => {
    if (!window.confirm(`"${customerName}" müşterisine yapılan ziyareti silmek istediğinizden emin misiniz?`)) {
      return;
    }

    try {
      await deleteVisit(visitId);
      toast.success('Ziyaret silindi');
      loadVisits();
    } catch (error) {
      toast.error('Ziyaret silinirken hata oluştu');
    }
  };

  const handleEditClick = (visit) => {
    setEditingVisit(visit);
    setEditFormData({
      notes: visit.notes || '',
      sale_made: visit.sale_made
    });
    setShowEditDialog(true);
  };

  const handleUpdateVisit = async () => {
    try {
      await updateVisit(editingVisit.id, {
        customer_id: editingVisit.customer_id,
        notes: editFormData.notes
      });
      toast.success('Ziyaret güncellendi');
      setShowEditDialog(false);
      loadVisits();
    } catch (error) {
      toast.error('Ziyaret güncellenirken hata oluştu');
    }
  };

  if (loading) {
    return <div className="text-center py-12">Yükleniyor...</div>;
  }

  return (
    <div className="space-y-4" data-testid="visits-page">
      <PageHeader
        icon={ClipboardList}
        title="Ziyaretler"
        subtitle={`Toplam ${visits.length} ziyaret`}
      >
        <Button
          onClick={handleExportExcel}
          className="bg-white text-green-600 hover:bg-green-50 shadow-md"
          data-testid="export-visits-button"
        >
          <Download className="w-4 h-4 mr-2" />
          Excel'e Aktar
        </Button>
      </PageHeader>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-lg p-4 space-y-4">
        {/* Arama Kutusu */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <Input
            type="text"
            placeholder="Müşteri adı, temsilci veya not ile ara..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        <div className="flex space-x-4">
          <div className="flex-1">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger data-testid="time-filter-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Ziyaretler</SelectItem>
                <SelectItem value="7">Son 7 Gün</SelectItem>
                <SelectItem value="30">Son 30 Gün</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Temsilci filtresi - admin, abonelik, temsilci için */}
          {['admin', 'abonelik', 'temsilci'].includes(user?.role) && (
            <div className="flex-1">
              <Select value={repFilter} onValueChange={setRepFilter}>
                <SelectTrigger data-testid="rep-filter-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Temsilciler</SelectItem>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.username}>{u.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex-1">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="status-filter-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Durumlar</SelectItem>
                <SelectItem value="visit">Ziyaret</SelectItem>
                <SelectItem value="sale">Satış Yapıldı</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex-1">
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger>
                <SelectValue placeholder="Sırala" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date-desc">Tarih (Yeni-Eski)</SelectItem>
                <SelectItem value="date-asc">Tarih (Eski-Yeni)</SelectItem>
                <SelectItem value="customer-asc">Müşteri (A-Z)</SelectItem>
                <SelectItem value="customer-desc">Müşteri (Z-A)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Visits List */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full table-auto" style={{ minWidth: '900px' }}>
            <thead className="progo-gradient text-gray-800">
              <tr>
                <th className="px-4 py-3 text-left whitespace-nowrap" style={{ minWidth: '140px' }}>Tarih</th>
                <th className="px-4 py-3 text-left whitespace-nowrap" style={{ minWidth: '180px' }}>Müşteri</th>
                <th className="px-4 py-3 text-left whitespace-nowrap" style={{ minWidth: '120px' }}>Temsilci</th>
                <th className="px-4 py-3 text-left whitespace-nowrap" style={{ minWidth: '200px' }}>Notlar</th>
                <th className="px-3 py-3 text-center whitespace-nowrap" style={{ minWidth: '100px' }}>Durum</th>
                <th className="px-4 py-3 text-center whitespace-nowrap" style={{ minWidth: '160px' }}>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                // Arama ve durum filtrelerini uygula
                const filteredVisits = visits.filter(visit => {
                  // Durum filtresi
                  if (statusFilter === 'sale' && visit.sale_made !== true) return false;
                  if (statusFilter === 'visit' && visit.sale_made === true) return false;
                  
                  // Arama filtresi
                  if (searchQuery.trim()) {
                    const query = searchQuery.toLowerCase();
                    return (
                      visit.customer_name?.toLowerCase().includes(query) ||
                      visit.representative?.toLowerCase().includes(query) ||
                      visit.notes?.toLowerCase().includes(query)
                    );
                  }
                  
                  return true;
                });

                if (filteredVisits.length === 0) {
                  return (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                        Henüz ziyaret kaydı yok
                      </td>
                    </tr>
                  );
                }

                const paginatedVisits = filteredVisits.slice(
                  (currentPage - 1) * itemsPerPage,
                  currentPage * itemsPerPage
                );

                return (
                  <>
                    {paginatedVisits.map((visit, index) => (
                      <tr key={visit.id} className="border-b hover:bg-gray-50 transition-colors" data-testid={`visit-row-${index}`}>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex items-center space-x-2">
                            <Calendar className="w-3 h-3 text-gray-400" />
                            <span className="whitespace-nowrap">{format(new Date(visit.visit_date), 'dd MMM yyyy HH:mm', { locale: tr })}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 font-medium text-sm">{visit.customer_name}</td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex items-center space-x-2">
                            <User className="w-3 h-3 text-gray-400" />
                            <span>{visit.representative}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-600 text-sm max-w-xs truncate">{visit.notes || '-'}</td>
                        <td className="px-3 py-3 text-center">{visit.sale_made ? (
                            <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                              Satış Yapıldı
                            </span>
                          ) : (
                            <span className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm font-medium">
                              Ziyaret
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex items-center justify-center space-x-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditClick(visit)}
                              data-testid={`edit-visit-${index}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            {/* Silme butonu - admin, abonelik, temsilci için */}
                            {['admin', 'abonelik', 'temsilci'].includes(user?.role) && (
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDeleteVisit(visit.id, visit.customer_name)}
                                data-testid={`delete-visit-${index}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </>
                );
              })()}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {(() => {
          const filteredVisits = visits.filter(visit => {
            if (statusFilter === 'all') return true;
            if (statusFilter === 'sale') return visit.sale_made === true;
            if (statusFilter === 'visit') return visit.sale_made !== true;
            return true;
          });

          if (filteredVisits.length <= itemsPerPage) return null;

          return (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <div className="text-sm text-gray-600">
                Toplam {filteredVisits.length} kayıttan {Math.min((currentPage - 1) * itemsPerPage + 1, filteredVisits.length)}-{Math.min(currentPage * itemsPerPage, filteredVisits.length)} arası gösteriliyor
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  Önceki
                </Button>
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.ceil(filteredVisits.length / itemsPerPage) }, (_, i) => i + 1)
                    .filter(page => {
                      const totalPages = Math.ceil(filteredVisits.length / itemsPerPage);
                      return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                    })
                    .map((page, idx, array) => (
                      <React.Fragment key={page}>
                        {idx > 0 && array[idx - 1] !== page - 1 && (
                          <span className="px-2 text-gray-400">...</span>
                        )}
                        <Button
                          variant={currentPage === page ? "default" : "outline"}
                          size="sm"
                          onClick={() => setCurrentPage(page)}
                          className={currentPage === page ? "progo-bg" : ""}
                        >
                          {page}
                        </Button>
                      </React.Fragment>
                    ))}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(Math.ceil(filteredVisits.length / itemsPerPage), prev + 1))}
                  disabled={currentPage === Math.ceil(filteredVisits.length / itemsPerPage)}
                >
                  Sonraki
                </Button>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Edit Visit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ziyaret Düzenle</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Notlar</Label>
              <Textarea
                value={editFormData.notes}
                onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                rows={4}
                data-testid="edit-visit-notes"
              />
            </div>
            <div className="flex justify-end space-x-3">
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>İptal</Button>
              <Button onClick={handleUpdateVisit} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-visit-edit">
                Kaydet
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Visits;